import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["container", "template", "fileInput", "summary", "autoBadge", "roomType"]
  static values = { classifyUrl: String }

  connect() {
    this.recomputeGroups()
  }

  pickFolder(event) {
    const input = event.currentTarget
    const files = Array.from(input.files || []).filter(this.isImage)
    if (!files.length) return

    files.sort((a, b) => {
      const ap = a.webkitRelativePath || a.name
      const bp = b.webkitRelativePath || b.name
      return ap.localeCompare(bp, undefined, { numeric: true, sensitivity: "base" })
    })

    files.forEach((file) => {
      const row = this.appendRow({ file, label: this.deriveLabel(file) })
      // Auto-detect each room's type from the photo (Gemini vision).
      if (row) this._classifyRow(row, file)
    })

    if (this.hasSummaryTarget) {
      const total = this.containerTarget.children.length
      this.summaryTarget.textContent = `${total} room${total === 1 ? "" : "s"} attached — room types are being auto-detected…`
    }

    input.value = ""
    this.recomputeGroups()
  }

  // Per-row "Choose File" selection — classify that single photo's room.
  fileChanged(event) {
    const input = event.currentTarget
    const file = input.files && input.files[0]
    if (!file || !this.isImage(file)) return
    const row = input.closest("[data-row]")
    if (row) this._classifyRow(row, file)
  }

  _propertyType() {
    const checked = this.element.querySelector('input[name="single_generation[property_type]"]:checked')
    return checked ? checked.value : ""
  }

  _csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content || ""
  }

  // POST the photo to the classifier and set the row's room-type select on a
  // confident match. Best-effort: silent on any failure, operator can edit.
  async _classifyRow(row, file) {
    if (!this.hasClassifyUrlValue || !this.classifyUrlValue || !file) return
    const select = row.querySelector("select[name$='[room_type]']")
    if (!select) return
    const badge = row.querySelector("[data-room-references-target='autoBadge']")

    try {
      const fd = new FormData()
      fd.append("file", file)
      fd.append("property_type", this._propertyType())
      const resp = await fetch(this.classifyUrlValue, {
        method: "POST",
        headers: { "X-CSRF-Token": this._csrfToken(), Accept: "application/json" },
        body: fd
      })
      if (!resp.ok) return
      const data = await resp.json()
      if (!data || !data.room_type) return
      const opt = Array.from(select.options).find((o) => o.value === data.room_type)
      if (!opt) return
      select.value = data.room_type
      if (badge) badge.hidden = false
    } catch (_) {
      // best-effort — leave the default room type in place
    }
  }

  appendRow({ file = null, label = "" } = {}) {
    const index = Date.now() + Math.floor(Math.random() * 1000)
    const html = this.templateTarget.innerHTML.replace(/__INDEX__/g, index)
    const wrapper = document.createElement("div")
    wrapper.innerHTML = html.trim()
    const row = wrapper.firstElementChild
    this.containerTarget.appendChild(row)

    if (label) {
      const labelInput = row.querySelector("input[name$='[label]']")
      if (labelInput) labelInput.value = label
    }
    if (file) {
      const fileInput = row.querySelector("input[type='file']")
      const dt = new DataTransfer()
      dt.items.add(file)
      fileInput.files = dt.files

      const thumb = row.querySelector("img.thumb")
      if (thumb) {
        thumb.src = URL.createObjectURL(file)
        thumb.hidden = false
      }
    }
    return row
  }

  remove(event) {
    event.preventDefault()
    const row = event.currentTarget.closest("[data-row]")
    if (!row) return
    const destroy = row.querySelector("input[name$='[_destroy]']")
    if (destroy) {
      destroy.value = "1"
      row.style.display = "none"
    } else {
      row.remove()
    }
    this.refreshSummary()
    this.recomputeGroups()
  }

  sameAsAboveChanged() {
    this.recomputeGroups()
  }

  // Walk visible rows top-to-bottom and assign view_group based on
  // the "Same room as ↑" checkbox state of each row.
  //
  // Row N with checkbox checked → joins the group of row N-1 (creating
  // a fresh group id if N-1 has none yet, so both rows share it).
  // Row N with checkbox unchecked → leaves the group; view_group cleared.
  // The first visible row's checkbox is meaningless and forced off.
  recomputeGroups() {
    const visibleRows = this._visibleRows()
    let lastGroup = null

    visibleRows.forEach((row, index) => {
      const checkbox = row.querySelector("[data-room-references-target='sameAsAbove']")
      const groupInput = row.querySelector("[data-room-references-target='viewGroup']")
      const badge = row.querySelector("[data-room-references-target='groupBadge']")
      const label = row.querySelector("[data-room-references-target='sameAsAboveLabel']")

      // First row can never be "same as above".
      if (index === 0) {
        if (checkbox) {
          checkbox.checked = false
          checkbox.disabled = true
        }
        if (label) label.style.opacity = "0.4"
        if (groupInput) groupInput.value = ""
        if (badge) badge.style.display = "none"
        lastGroup = null
        return
      }

      if (checkbox) checkbox.disabled = false
      if (label) label.style.opacity = ""

      if (checkbox && checkbox.checked) {
        // Make sure the row above has a group id; if not, mint one and assign it.
        if (!lastGroup) {
          lastGroup = this._newGroupId()
          const prevGroupInput = visibleRows[index - 1].querySelector("[data-room-references-target='viewGroup']")
          const prevBadge = visibleRows[index - 1].querySelector("[data-room-references-target='groupBadge']")
          if (prevGroupInput) prevGroupInput.value = lastGroup
          if (prevBadge) prevBadge.style.display = ""
        }
        if (groupInput) groupInput.value = lastGroup
        if (badge) badge.style.display = ""
      } else {
        if (groupInput) groupInput.value = ""
        if (badge) badge.style.display = "none"
        lastGroup = null
      }
    })

    // After the walk, drop the badge from any single-member group (a row
    // whose group id is no longer shared by anyone else).
    const groupCounts = {}
    visibleRows.forEach((row) => {
      const gi = row.querySelector("[data-room-references-target='viewGroup']")
      if (gi && gi.value) groupCounts[gi.value] = (groupCounts[gi.value] || 0) + 1
    })
    visibleRows.forEach((row) => {
      const gi = row.querySelector("[data-room-references-target='viewGroup']")
      const badge = row.querySelector("[data-room-references-target='groupBadge']")
      if (gi && gi.value && groupCounts[gi.value] < 2) {
        gi.value = ""
        if (badge) badge.style.display = "none"
      }
    })
  }

  _visibleRows() {
    return Array.from(this.containerTarget.querySelectorAll("[data-row]"))
      .filter((r) => r.style.display !== "none")
  }

  _newGroupId() {
    if (window.crypto && window.crypto.randomUUID) return window.crypto.randomUUID()
    return "g-" + Date.now().toString(36) + "-" + Math.floor(Math.random() * 1e9).toString(36)
  }

  isImage(file) {
    return /^image\/(jpeg|png|webp)$/.test(file.type) || /\.(jpe?g|png|webp)$/i.test(file.name)
  }

  deriveLabel(file) {
    const name = file.name.replace(/\.[^.]+$/, "")
    return name.replace(/[_-]+/g, " ").replace(/\s+/g, " ").trim()
  }

  refreshSummary() {
    if (!this.hasSummaryTarget) return
    const visible = Array.from(this.containerTarget.children).filter((r) => r.style.display !== "none").length
    this.summaryTarget.textContent = visible === 0
      ? "Pick a folder of room photos to start."
      : `${visible} room${visible === 1 ? "" : "s"} attached. Tag each with its function.`
  }
}
